import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';

import { configPlayer, mapAssetType } from './video-utils';
import { DEFAULT_CTA } from './metadata';
import closeIcon from './media/close.svg';
import passportIcon from './media/new_passport.svg';
import styles from './video-player.scss';
import Select from 'react-select';
import { fetchShowSeasons } from './season-utils';
import { objectExistsInArray } from 'utils';
import { ALL_SEASONS } from './constants';

const VideoPlayer = (props) => {
    // Destructure props and apply default values for defaultProps
    const {
        API,
        componentId,
        handleCTAChange,
        CallToAction,
        showVideo,
        playerClass = '', // Default prop
        videoClose,
        showCta,
        cta = DEFAULT_CTA, // Default prop
        settings,
        callsign: propCallsign, // Renamed prop to avoid conflict with local variable
        currentSite,
        currentVideo,
        isSomethingDragging = false, // Default prop
        reference,
        isPillButtonAvailable,
        handleSelectSeasonFilter,
        site,
        selectedSeasonFilter,
        showId
    } = props;

    // 1. State: Replaces this.state
    const [seasons, setSeasons] = useState([]);

    // Get current site ID (used in effects and callbacks)
    const currentSiteId = currentSite?.siteId ? currentSite?.siteId : site?.id;
    const {
        selectedShows,
        seasonFilter,
        selectedSeasons,
        videoType,
        selectedRegions,
        includePassportVideos
    } = settings;

    // 2. Instance Methods: Converted to utility functions/useCallback

    // Helper to get the correct callsign
    const getCallsign = useCallback(() => {
        const siteType = currentSite?.siteType;
        const station = currentSite?.station;

        if (propCallsign) {
            // Use the renamed prop
            return propCallsign;
        }
        if (siteType === 'station' && station && station.callSign) {
            return station.callSign;
        }
        return '';
    }, [propCallsign, currentSite]); // Updated dependency

    // Helper to fetch and set seasons state
    const getShowSeasons = useCallback(
        (siteId, selectedShows) => {
            const { selectedSeasons, includePassportVideos, selectedRegions, videoType } = settings;
            let localSelectedSeasonFilter = selectedSeasonFilter;

            // NOTE: The original component references a 'this.showLoading' function
            // which is not defined. We'll omit it here or assume it's a function that
            // is passed (e.g., as a prop). For this refactor, we will omit the call.

            let newSeasons = [];

            if (
                (selectedSeasons &&
                    selectedSeasons.length > 0 &&
                    selectedSeasons[0].value !== ALL_SEASONS.value) ||
                selectedShows.length > 1
            ) {
                newSeasons = [ALL_SEASONS, ...selectedSeasons];
                if (
                    localSelectedSeasonFilter &&
                    !objectExistsInArray(localSelectedSeasonFilter, newSeasons)
                ) {
                    localSelectedSeasonFilter = ALL_SEASONS;
                }
                // If the filter needs to be reset, notify the parent (which manages selectedSeasonFilter)
                if (
                    localSelectedSeasonFilter === ALL_SEASONS &&
                    selectedSeasonFilter !== ALL_SEASONS
                ) {
                    handleSelectSeasonFilter(ALL_SEASONS);
                }
                setSeasons(newSeasons);
            } else if (selectedShows.length === 1) {
                // Only fetch if there's exactly one show selected and we aren't using pre-selected seasons
                fetchShowSeasons(API, siteId, selectedShows[0].id, {
                    selectedSeasons,
                    includePassportVideos,
                    selectedRegions,
                    videoType
                }).done((seasons) => {
                    // Filter out seasons without a valid label
                    newSeasons = seasons.filter((s) => s.label);
                    setSeasons(newSeasons);
                });
            }
        },
        [API, settings, selectedSeasonFilter, handleSelectSeasonFilter]
    );

    useEffect(() => {
        if (seasonFilter) {
            getShowSeasons(currentSiteId, selectedShows);
        }
    }, [
        seasonFilter,
        selectedShows,
        selectedSeasons,
        videoType,
        selectedRegions,
        includePassportVideos,
        currentSiteId
    ]);

    // Render Guards (Replaces early returns in render())
    if (playerClass && !showVideo) {
        return null;
    }
    if (!currentVideo) {
        return null;
    }

    const {
        player,
        displayCTA,
        displayShowCTA,
        displayVideoTitle,
        displayShowTitle,
        displayVideoMeta,
        seasonFilter: isSeasonFilterActive
    } = settings;

    const {
        showTitle,
        videoTitle,
        duration,
        date,
        rating,
        type,
        longDescription,
        id,
        isPassportVideo,
        seasonInfo
    } = currentVideo;

    // Logic for videoMeta remains the same
    const videoMeta = [mapAssetType(type), seasonInfo, duration, date, rating]
        .filter((item) => item && String(item).trim() !== '')
        .join(' | ');

    // Get configuration for player (using the functional call)
    const finalCallsign = getCallsign();
    // When autoplay is true, also set muted=1 to prevent browser autoplay policy violations
    const config = {
        ...(finalCallsign ? { ...player, callsign: finalCallsign } : player),
        ...(player.autoplay && { muted: true })
    };
    return (
        <div
            ref={reference}
            className={classnames({
                [styles.videoPlayer]: true,
                'video-player': true,
                [playerClass]: true
            })}
            role="region"
            aria-label="Video Player">
            {/* Screen-reader-only live region that announces video changes */}
            <div className="sr-only" aria-live="polite" aria-atomic="true" role="status">
                {`Now playing: ${videoTitle || ''}${showTitle ? ` from ${showTitle}` : ''}`}
            </div>
            {playerClass && (
                <button
                    className="close"
                    onClick={videoClose}
                    aria-label="Close video player"
                    type="button">
                    <img src={closeIcon} alt="" aria-hidden="true" />
                </button>
            )}
            <div className="left-side">
                <div
                    className={classnames({
                        'responsive-player-wrapper': true,
                        'top-bar': player.topbar,
                        'shape-placeholder-fill': true
                    })}>
                    {/* Render player iframe if available and not dragging */}
                    {currentVideo.playerIframe && !isSomethingDragging && (
                        <div
                            className="player"
                            dangerouslySetInnerHTML={{
                                __html: configPlayer(currentVideo.playerIframe, config)
                            }}
                        />
                    )}
                </div>
            </div>
            <div className="right-side">
                <div className="inner-left">
                    {displayShowTitle && (
                        <h3 className="text-placeholder show-title">{showTitle}</h3>
                    )}
                    {displayVideoTitle && (
                        <h4 className="text-placeholder video-title">{videoTitle}</h4>
                    )}
                    {displayVideoMeta && (
                        <div className="text-placeholder video-meta">
                            {isPassportVideo && (
                                <img
                                    className="passport-icon"
                                    src={passportIcon}
                                    alt="Passport icon"
                                />
                            )}
                            {videoMeta}
                        </div>
                    )}
                    <p className="text-placeholder-long double description">{longDescription}</p>
                </div>
                <div className="inner-right">
                    {displayCTA && (
                        <CallToAction
                            {...props}
                            componentId={componentId}
                            handleCTAChange={handleCTAChange}
                            videoId={id}
                            cta={cta}
                            isShowCTAAvailable={false}
                            isPillButtonAvailable={isPillButtonAvailable}
                        />
                    )}
                    {displayShowCTA && (
                        <CallToAction
                            {...props}
                            componentId={componentId}
                            handleCTAChange={handleCTAChange}
                            showId={showId}
                            cta={showCta}
                            isShowCTAAvailable={true}
                            isPillButtonAvailable={isPillButtonAvailable}
                        />
                    )}
                    {isSeasonFilterActive && (
                        <Select
                            aria-label="Season Filter"
                            options={seasons}
                            isClearable={false}
                            name="seasonFilter"
                            placeholder="Select season"
                            value={selectedSeasonFilter}
                            isSearchable={false}
                            onChange={handleSelectSeasonFilter}
                            classNamePrefix="react-select"
                            className={`season-filter ${
                                isPillButtonAvailable ? 'pill-button-component' : ''
                            }`}
                            onFocus={() => {}}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const menuOpen =
                                        !!e.currentTarget.querySelector('[class*="menu-list"]');
                                    if (!menuOpen) {
                                        const control =
                                            e.currentTarget.querySelector('[class*="control"]');
                                        if (control) {
                                            setTimeout(() => {
                                                control.dispatchEvent(
                                                    new MouseEvent('mousedown', {
                                                        bubbles: true
                                                    })
                                                );
                                            }, 0);
                                        }
                                    }
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// Define PropTypes outside the function
VideoPlayer.propTypes = {
    API: PropTypes.object,
    componentId: PropTypes.string.isRequired,
    handleCTAChange: PropTypes.func.isRequired,
    CallToAction: PropTypes.func.isRequired,
    showVideo: PropTypes.bool.isRequired,
    playerClass: PropTypes.string,
    videoClose: PropTypes.func.isRequired,
    showCta: PropTypes.shape({
        link: PropTypes.object,
        url: PropTypes.string,
        text: PropTypes.string,
        textPlaceholder: PropTypes.string
    }),
    cta: PropTypes.shape({
        link: PropTypes.object,
        url: PropTypes.string,
        text: PropTypes.string,
        textPlaceholder: PropTypes.string
    }),
    settings: PropTypes.shape({
        displayCTA: PropTypes.bool.isRequired,
        displayShowCTA: PropTypes.bool.isRequired,
        selectedShows: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                links: PropTypes.array
            })
        ),
        selectedSeasons: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.string.isRequired
            })
        ),
        player: PropTypes.shape({
            topbar: PropTypes.bool.isRequired,
            autoplay: PropTypes.bool.isRequired
        }).isRequired,
        displayVideoTitle: PropTypes.bool.isRequired,
        displayShowTitle: PropTypes.bool.isRequired,
        displayVideoMeta: PropTypes.bool.isRequired,
        seasonFilter: PropTypes.bool,
        selectedRegions: PropTypes.array,
        videoType: PropTypes.string.isRequired,
        includePassportVideos: PropTypes.bool.isRequired
    }),
    callsign: PropTypes.string,
    currentSite: PropTypes.object,
    currentVideo: PropTypes.object,
    isSomethingDragging: PropTypes.bool.isRequired,
    reference: PropTypes.object,
    isPillButtonAvailable: PropTypes.bool,
    handleSelectSeasonFilter: PropTypes.func,
    site: PropTypes.object,
    selectedSeasonFilter: PropTypes.object,
    showId: PropTypes.string
};

export default VideoPlayer;
